import {
  RefObject, useContext, useEffect, useRef,
} from 'react'
import { toast } from 'react-toastify'

import { digObject } from '@domain-group/javascript-utils'
import {
  useDeepEffect, useForm, useRealtimeSubscription, useSetState, useUniqueId,
} from '@domain-group/react-hooks'

import useCurrentUser from '@hooks/useCurrentUser'

import RealtimeContext from '@contexts/realtimeContext'

import { generateReportItems, queueReportExport } from '@functions/reporting'

import reportModels from '@models/reports'
import type { CampaignModel } from '@models/campaign'
import type { ReportDefaultStateType, ReportKeyType } from '@models/reports'

import type { ActionResult } from '@redux/modules/types'

type GenerateUrlsOptions = {
  prefixRoute?: string,
}

export const generateUrls = (options?: GenerateUrlsOptions) => {
  const { prefixRoute = 'systemManager' } = options || {}

  return {
    reportingIndexUrl: `#/${prefixRoute}/reporting`,
  }
}

const buildInitReport = (defaultFormState: ReportDefaultStateType, defaultValues: ReportDefaultStateType) => (
  Object.keys(defaultFormState).reduce((initReport: ReportDefaultStateType, key) => {
    // @ts-expect-error TS7053
    if (key in defaultValues) initReport[key] = defaultValues[key]
    return initReport
  }, {})
)

type DefaultState = {
  isSetup: boolean,
  loading: boolean,
  selectedReportItemKey: Partial<ReportKeyType> | undefined,
}

type SetState = (payload: Partial<DefaultState>) => void

type GenerateReportParams = {
  campaign: CampaignModel,
  currentUserPayload: ReturnType<typeof useCurrentUser>,
  formStateRef: RefObject<ReportDefaultStateType>,
  payload?: any,
  selectedReportItemKey: ReportKeyType | undefined,
  setState: SetState,
  uuid: string,
}

const generateReport = (
  params: GenerateReportParams,
): Promise<ActionResult<{ queued: true }>> => {
  const {
    currentUserPayload, formStateRef, payload, selectedReportItemKey, setState, uuid,
  } = params

  const {
    end_date, start_date,
  } = formStateRef.current!

  setState({ loading: true })

  const { currentUser: { association_id, association_type } } = currentUserPayload

  const reportItems = generateReportItems(formStateRef.current!)
  const selectedItem = reportItems.find(item => item.key === selectedReportItemKey)

  const {
    buildReportTitle, url, params: selectedItemParams = {},
  } = selectedItem || {}

  if (url){
    const exportUrl = `${url}.json`
    const options = {
      attachable_id: association_id,
      attachable_type: association_type,
      end_date,
      key: uuid,
      report_type: buildReportTitle ? buildReportTitle(payload) : selectedReportItemKey,
      start_date,
      ...selectedItemParams,
    }

    return queueReportExport(exportUrl, options).then((response) => {
      setState({ loading: false })
      return response
    })
  }

  setState({ loading: false })

  return new Promise((resolve) => {
    resolve({ success: false, errors: ['Generate Report Failed'] })
  })
}

type RealtimeDataType = {
  payload?: {
    errors?: string[],
    links?: {
      original_url?: string,
    },
  },
}

const processingComplete = (data: RealtimeDataType) => {
  const link = digObject(data, 'payload.links.original_url', '')
  const errors = digObject(data, 'payload.errors', [])

  if (link) window.open(link, '_blank')
  if (errors) toast.warn(errors[0])
}

const defaultState: DefaultState = {
  isSetup: false,
  loading: false,
  selectedReportItemKey: undefined,
}

export type UseReportingOptions = {
  allowedReports?: ReportKeyType[],
  formDeps?: string[] | number[],
  defaultValues?: ReportDefaultStateType,
}

function useReporting(options: UseReportingOptions = {}) {
  const {
    allowedReports = [], formDeps = [], defaultValues = {},
  } = options

  const realtimeContext = useContext(RealtimeContext)
  // @ts-expect-error TS2339
  const { clientRef } = realtimeContext

  const [state, setState] = useSetState(defaultState)
  const { isSetup, loading, selectedReportItemKey } = state

  // Generate and filter report items
  const reportItems = generateReportItems({})

  const filteredReportItems = reportItems.filter(item => allowedReports.includes(item.key))

  const selectedReportItem = filteredReportItems.find(item => item.key === selectedReportItemKey)

  // @ts-expect-error TS2538
  const defaultFormState = digObject(reportModels[selectedReportItemKey], 'defaultState', {})
  // @ts-expect-error TS2538
  const requiredFields = digObject(reportModels[selectedReportItemKey], 'requiredFields', [])

  const initReport = buildInitReport(defaultFormState, defaultValues)

  const reportingForm = useForm(
    defaultFormState,
    { entity: initReport, requiredFields },
    [isSetup, selectedReportItem?.key, ...formDeps],
  )

  const { entityState, saveEnabled } = reportingForm

  const formStateRef = useRef({})
  formStateRef.current = entityState

  const fieldKeys = Object.keys(entityState)

  const currentUserPayload = useCurrentUser()

  // To trigger rerun of Errors memo in useForm
  useDeepEffect(() => {
    if (fieldKeys.length && !isSetup){
      const keysArray = Object.keys(initReport) as (keyof ReportDefaultStateType)[]
      const stateIsSetup = keysArray.every(key => defaultFormState[key] === entityState[key])
      if (stateIsSetup) setState({ isSetup: true })
    }
  }, [fieldKeys, selectedReportItem?.key])

  // Auto select report of only one allowed
  useEffect(() => {
    if (allowedReports.length === 1) setState({ selectedReportItemKey: allowedReports[0] })
  }, [allowedReports.length])

  const [uuid] = useUniqueId({ persistKey: window.location.pathname })

  // Realtime/Websocket Connection
  useRealtimeSubscription({
    apiKey: process.env.REACT_APP_ABLY_PUBLIC_KEY,
    callbacks: {
      success: (data: RealtimeDataType) => processingComplete(data),
    },
    channelName: 'reports',
    clientRef,
    eventName: `report.${uuid}.build.complete`,
  }, [uuid])

  // @ts-expect-error TS2345
  const shouldShowInvoiceDate = ['order_totals_publication', 'order_totals_provider'].includes(selectedReportItemKey)
  const shouldShowSoldDate = ['order_reconciliation_by_agency', 'order_reconciliation_by_campaign'].includes(
    // @ts-expect-error TS2345
    selectedReportItemKey,
  )

  return {
    callbacks: {
      // @ts-expect-error TS2345
      generateReport: (payload?: any) => generateReport({
        currentUserPayload, formStateRef, payload, selectedReportItemKey, setState, uuid,
      }),
      setState,
      shouldRenderField: (key: string) => Object.keys(defaultFormState).includes(key),
    },
    initReport,
    shouldShowInvoiceDate,
    shouldShowSoldDate,
    loading,
    reportingForm,
    reportItems: filteredReportItems,
    saveEnabled: !!selectedReportItemKey && saveEnabled,
    selectedReportItemKey,
    strings: selectedReportItem?.strings || {},
    urls: generateUrls(),
  }
}

export type UseReportingPayload = ReturnType<typeof useReporting>

export default useReporting
